import { useMemo } from "react";
import { Activity } from "lucide-react";

/**
 * VirtualBiomechanicalMap — interpretação biomecânica separada da foto real.
 *
 * Em vez de pintar overlays sobre a fotografia do paciente, este componente
 * desenha avatares SVG vectoriais (frontal · lateral · posterior) e aplica
 * hotspots radiais sobre as zonas anatómicas em função do score por segmento
 * (`analysis.segments`) e da linha de gravidade (`analysis.plumb_line`).
 *
 * Estética: dashboard clínico premium · azul/cyan · glow subtil · gradiente
 * azul → verde → âmbar → vermelho · proporcional ao score · sem efeitos gaming.
 *
 * Sem cálculos novos · sem IA · sem dependências · pure derived view.
 * Reutiliza os mesmos campos que o resto da Análise Clínica.
 */

// Severidade → cor (paleta médica fria, evita "RGB gaming").
const TONES = {
  ok:       { core: "#34d399", outer: "#059669", label: "alinhado" },   // emerald
  mild:     { core: "#facc15", outer: "#ca8a04", label: "ligeiro" },    // amber
  moderate: { core: "#fb923c", outer: "#c2410c", label: "moderado" },   // orange
  alert:    { core: "#fb7185", outer: "#be123c", label: "expressivo" }, // rose
};

function toneFor(score) {
  if (score == null) return null;
  if (score >= 88) return TONES.ok;
  if (score >= 75) return TONES.mild;
  if (score >= 60) return TONES.moderate;
  return TONES.alert;
}

// Coordenadas anatómicas relativas (viewBox 100×220) por vista.
// Pensadas para combinar com as silhuetas desenhadas abaixo.
const REGIONS = {
  front: {
    cervical:  { x: 50, y: 36 },
    shoulders: { x: 50, y: 60 },
    spine:     { x: 50, y: 95 },
    pelvis:    { x: 50, y: 130 },
    knees:     { x: 50, y: 170 },
  },
  side: {
    cervical:  { x: 50, y: 36 },
    thoracic:  { x: 52, y: 80 },
    lumbar:    { x: 48, y: 115 },
    pelvis:    { x: 50, y: 132 },
    knees:     { x: 52, y: 170 },
  },
  back: {
    cervical:  { x: 50, y: 36 },
    shoulders: { x: 50, y: 60 },
    scapula:   { x: 50, y: 80 },
    spine:     { x: 50, y: 100 },
    pelvis:    { x: 50, y: 130 },
    knees:     { x: 50, y: 170 },
  },
};

// Silhuetas SVG simples — minimal/vector, premium clínico.
const SILHOUETTES = {
  front: (
    <g stroke="var(--silhouette-stroke)" strokeWidth="0.6" fill="var(--silhouette-fill)" fillOpacity="0.55">
      <ellipse cx="50" cy="22" rx="9" ry="11" />
      <path d="M 41 32 Q 41 36 38 38 L 30 46 Q 24 50 24 60 L 27 70" fill="none" />
      <path d="M 59 32 Q 59 36 62 38 L 70 46 Q 76 50 76 60 L 73 70" fill="none" />
      <path d="M 38 38 L 30 46 Q 24 52 25 75 L 28 100 L 32 130 L 38 138 L 42 138 L 44 130 L 44 100" />
      <path d="M 62 38 L 70 46 Q 76 52 75 75 L 72 100 L 68 130 L 62 138 L 58 138 L 56 130 L 56 100" />
      <path d="M 44 100 L 56 100 L 58 138 L 42 138 Z" />
      <path d="M 38 138 L 35 175 L 40 198 L 45 198 L 47 175 L 47 138 Z" />
      <path d="M 62 138 L 65 175 L 60 198 L 55 198 L 53 175 L 53 138 Z" />
    </g>
  ),
  side: (
    <g stroke="var(--silhouette-stroke)" strokeWidth="0.6" fill="var(--silhouette-fill)" fillOpacity="0.55">
      <ellipse cx="50" cy="22" rx="8" ry="10" />
      <path d="M 50 32 Q 52 38 50 44 L 48 56" fill="none" />
      <path d="M 42 56 Q 38 70 42 100 L 44 130 L 48 138 L 54 138 L 58 130 L 60 100 Q 62 75 58 56 Q 54 50 50 50 Q 46 50 42 56 Z" />
      <path d="M 44 138 L 41 175 L 44 198 L 50 198 L 50 175 L 48 138 Z" />
      <path d="M 54 138 L 56 175 L 52 198 L 50 198" fill="none" />
    </g>
  ),
  back: (
    <g stroke="var(--silhouette-stroke)" strokeWidth="0.6" fill="var(--silhouette-fill)" fillOpacity="0.55">
      <ellipse cx="50" cy="22" rx="9" ry="11" />
      <path d="M 41 32 Q 41 36 38 38 L 30 46 Q 24 52 25 75 L 28 100 L 32 130 L 38 138 L 42 138 L 44 130 L 44 100" />
      <path d="M 59 32 Q 59 36 62 38 L 70 46 Q 76 52 75 75 L 72 100 L 68 130 L 62 138 L 58 138 L 56 130 L 56 100" />
      <path d="M 44 100 L 56 100 L 58 138 L 42 138 Z" />
      <path d="M 38 138 L 35 175 L 40 198 L 45 198 L 47 175 L 47 138 Z" />
      <path d="M 62 138 L 65 175 L 60 198 L 55 198 L 53 175 L 53 138 Z" />
      {/* coluna estilizada — leve, subtle */}
      <line x1="50" y1="35" x2="50" y2="135" stroke="var(--silhouette-stroke)" strokeWidth="0.6" strokeDasharray="1.5 2" />
    </g>
  ),
};

/**
 * Constrói lista de hotspots para a vista, mapeando segments[] → coordenadas.
 * Retorna { id, at:{x,y}, score, tone, label }.
 */
function buildHotspots(view, analysis) {
  if (!analysis?.segments) return [];
  const seg = analysis.segments;
  const map = REGIONS[view] || {};
  const out = [];

  const push = (id, segKey, label) => {
    const at = map[id];
    const s = seg[segKey];
    if (!at || !s) return;
    const tone = toneFor(s.score);
    if (!tone) return;
    out.push({ id, at, score: s.score, tone, label });
  };

  if (view === "side") {
    push("cervical", "cervical", "Cervical");
    push("thoracic", "spine_lateral", "Dorsal");
    if (!seg.spine_lateral && seg.spine) push("thoracic", "spine", "Dorsal");
    push("lumbar", "spine_lateral", "Lombar");
    if (!seg.spine_lateral && seg.spine) push("lumbar", "spine", "Lombar");
    push("pelvis", "pelvis", "Bacia");
    push("knees", "knees", "Joelho");
    return out;
  }

  push("cervical", "cervical", "Cervical");
  push("shoulders", "shoulders", "Ombros");
  if (view === "back") push("scapula", "scapula", "Omoplatas");
  push("spine", "spine", "Coluna");
  push("pelvis", "pelvis", "Bacia");
  push("knees", "knees", "Joelhos");
  return out;
}

function tierFromAnalysis(analysis) {
  const score = analysis?.global_score;
  const patterns = analysis?.patterns?.length || 0;
  if (patterns >= 3 || (typeof score === "number" && score < 75)) return "expressivo";
  if (patterns >= 1 || (typeof score === "number" && score < 88)) return "moderado";
  return "discreto";
}

const TIER_RADIUS = {
  discreto:   { core: 6.5, ring: 8 },
  moderado:   { core: 7.5, ring: 9 },
  expressivo: { core: 9,   ring: 11 },
};

function AvatarPanel({ view, analysis }) {
  const hotspots = useMemo(() => buildHotspots(view, analysis), [view, analysis]);
  const tier = useMemo(() => tierFromAnalysis(analysis), [analysis]);
  const radii = TIER_RADIUS[tier];
  const labelView = { front: "Frontal", side: "Lateral", back: "Posterior" }[view];

  return (
    <div
      data-testid={`virtual-avatar-${view}`}
      className="relative rounded-xl border border-slate-800/80 bg-gradient-to-b from-slate-950 to-slate-900/60 overflow-hidden"
      style={{ aspectRatio: "100 / 220", boxShadow: "inset 0 0 60px rgba(2,6,23,0.85), 0 0 0 1px rgba(56,189,248,0.06)" }}
    >
      <svg viewBox="0 0 100 220" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          {hotspots.map((h, i) => (
            <radialGradient key={`g-${view}-${h.id}-${i}`} id={`vbm-${view}-${h.id}-${i}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor={h.tone.core}  stopOpacity="0.78" />
              <stop offset="48%" stopColor={h.tone.outer} stopOpacity="0.32" />
              <stop offset="100%" stopColor={h.tone.outer} stopOpacity="0" />
            </radialGradient>
          ))}
          <filter id={`vbm-blur-${view}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>

        {/* Silhueta vetorial */}
        {SILHOUETTES[view]}

        {/* Eixo gravitacional vertical subtil (apenas vista lateral) */}
        {view === "side" && (
          <line x1="50" y1="0" x2="50" y2="220" stroke="#38bdf8" strokeOpacity="0.18" strokeDasharray="1.5 3" strokeWidth="0.5" />
        )}

        {/* Hotspots por região — radiais com glow subtil + anel medical-tech */}
        <g style={{ mixBlendMode: "screen" }}>
          {hotspots.map((h, i) => (
            <g key={`h-${view}-${h.id}-${i}`} data-testid={`vbm-hotspot-${view}-${h.id}`}>
              <circle
                cx={h.at.x}
                cy={h.at.y}
                r={radii.core}
                fill={`url(#vbm-${view}-${h.id}-${i})`}
                filter={`url(#vbm-blur-${view})`}
              />
              <circle
                cx={h.at.x}
                cy={h.at.y}
                r={radii.ring}
                fill="none"
                stroke={h.tone.core}
                strokeWidth="0.25"
                strokeOpacity="0.45"
              />
            </g>
          ))}
        </g>
      </svg>

      {/* Etiqueta da vista */}
      <div className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.18em] text-sky-300/80 font-mono">
        {labelView}
      </div>
      {/* Estado vazio */}
      {hotspots.length === 0 && (
        <div className="absolute bottom-3 left-0 right-0 text-center text-[10px] text-slate-500 italic">
          Sem leituras significativas
        </div>
      )}
    </div>
  );
}

export default function VirtualBiomechanicalMap({ analysis }) {
  const tier = tierFromAnalysis(analysis);
  // Resumo das regiões mais ativas (top 4) para legenda lateral.
  const allHits = useMemo(() => {
    if (!analysis) return [];
    const front = buildHotspots("front", analysis);
    const side = buildHotspots("side", analysis);
    const back = buildHotspots("back", analysis);
    const merged = new Map();
    for (const h of [...front, ...side, ...back]) {
      const cur = merged.get(h.label);
      if (!cur || (h.score < cur.score)) merged.set(h.label, h);
    }
    return [...merged.values()].sort((a, b) => a.score - b.score).slice(0, 4);
  }, [analysis]);
  if (!analysis) return null;

  return (
    <div
      data-testid="virtual-biomech-map"
      data-tier={tier}
      className="rounded-2xl border border-sky-500/20 bg-gradient-to-br from-slate-950/70 to-sky-950/10 p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-sky-300" strokeWidth={1.5} />
          <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">
            Mapa Biomecânico Virtual
          </div>
          <span
            data-testid="vbm-tier-badge"
            className={`ml-1 text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-[0.18em] ${
              tier === "expressivo" ? "border-rose-500/40 text-rose-200 bg-rose-500/10"
              : tier === "moderado" ? "border-amber-500/40 text-amber-200 bg-amber-500/10"
              : "border-emerald-500/30 text-emerald-200 bg-emerald-500/5"
            }`}
          >
            {tier}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-slate-500">
          Interpretação · vetor · não fotografia
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-1 italic">
        Visualização funcional separada da observação real. Densidade adaptada ao perfil postural.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <AvatarPanel view="front" analysis={analysis} />
        <AvatarPanel view="side"  analysis={analysis} />
        <AvatarPanel view="back"  analysis={analysis} />
      </div>

      {/* Legenda + resumo */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: TONES.ok.core }} /> alinhado</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: TONES.mild.core }} /> ligeiro</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: TONES.moderate.core }} /> moderado</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: TONES.alert.core }} /> expressivo</span>
        </div>
        {allHits.length > 0 && (
          <div className="text-[11px] text-slate-400" data-testid="vbm-summary">
            <span className="text-slate-500 uppercase tracking-[0.15em] text-[9px] mr-1.5">Mais expressivas:</span>
            {allHits.map((h, i) => (
              <span key={i} className="mr-2">
                <span className="text-slate-200">{h.label}</span>
                <span className="text-slate-500 ml-1">· {Math.round(h.score)}/100</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
